// Escolha seu plano (canvas 2.1): abas Mensal e Anual (com 4 mensalidades de desconto), abas Grátis, Bronze,
// Prata e Ouro, o que cada um tem e o botão de assinar, que leva direto ao Checkout da Stripe, sem tela de confirmar.
// No computador, os 4 cartões lado a lado, cada um com o seu botão, e a comparação aberta embaixo. Quem já assina
// vai para Trocar de plano, que mostra a diferença a pagar.
import { type ReactNode, useEffect, useState } from 'react';
import {
  A_VENDA, type Ciclo, MATRIZ, NOME_DO_PLANO, PLANOS, type Plano, type PlanoPago, ehPlanoPago, emReais, itensDoPlano,
  ordemDoPlano, parcelaDoAnual, precoEmReais,
} from '@truco6/planos';
import { api, dataCompleta } from '../comum/api.ts';
import { conta, type PerfilConta, type Sessao } from '../comum/conta.ts';
import { compra, itemDoPlano, medir } from '../comum/medicao.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { useComputador } from '../comum/Navegacao.tsx';
import { AbasCiclo, EmBreve, Icone, Moeda, PrecoDoCiclo, VantagemDoAnual, irParaCheckout, useEu } from './planos-comum.tsx';
import { toqueEmAssinar } from '../comum/limite.ts';
import { Tela, Topo } from './comum.tsx';

// evidencia: quem está no Grátis vê o Bronze com a borda laranja e o selo "Para jogar sem limite" (item 59.2).
export function CartaoPlano({ plano, ciclo, destaque = false, escolhido = false, evidencia = false, children }: {
  plano: Plano; ciclo: Ciclo; destaque?: boolean; escolhido?: boolean; evidencia?: boolean; children?: ReactNode;
}) {
  return (
    <article className={'cartao-plano plano-' + plano + (escolhido ? ' escolhido' : '') + (evidencia ? ' sem-limite' : '')} aria-label={'Plano ' + NOME_DO_PLANO[plano] + (ehPlanoPago(plano) ? ' ' + ciclo : '')}>
      {evidencia ? <span className="selo-topo selo-sem-limite">Para jogar sem limite</span> : destaque && <span className="selo-topo">Recomendado</span>}
      <div className="plano-cab">
        <Moeda plano={plano} tamanho={44} />
        <h2>{NOME_DO_PLANO[plano]}</h2>
        {ehPlanoPago(plano) ? <PrecoDoCiclo plano={plano} ciclo={ciclo} /> : <p className="preco">R$ 0</p>}
      </div>
      {ehPlanoPago(plano) && ciclo === 'anual' && <VantagemDoAnual plano={plano} />}
      <ul className="itens">
        {itensDoPlano(plano).map((i) => (
          <li key={i.recurso} className={i.emBreve ? 'depois' : undefined}>
            <Icone nome="checkCirculo" tamanho={20} />
            <span>{i.texto}</span>
            {i.emBreve && <EmBreve />}
          </li>
        ))}
      </ul>
      {children}
    </article>
  );
}

// No computador, a comparação fica aberta.
function Comparar({ ciclo, aberta = false }: { ciclo: Ciclo; aberta?: boolean }) {
  const linhas = Object.values(MATRIZ);
  return (
    <details className="cartao comparar" open={aberta}>
      <summary>Comparar todos os planos</summary>
      <div className="tabela-rolar">
        <table>
          <thead>
            <tr><th scope="col">Recurso</th>{PLANOS.map((p) => <th scope="col" key={p}>{NOME_DO_PLANO[p]}</th>)}</tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.nome}>
                <th scope="row">{l.nome}</th>
                {PLANOS.map((p) => <td key={p}>{l.celulas[p] || '·'}</td>)}
              </tr>
            ))}
            {ciclo === 'mensal' ? (
              <tr>
                <th scope="row">Preço por mês</th>
                {PLANOS.map((p) => <td key={p}>{ehPlanoPago(p) ? precoEmReais(p) : 'R$ 0'}</td>)}
              </tr>
            ) : (
              <>
                <tr>
                  <th scope="row">Preço por ano, à vista</th>
                  {PLANOS.map((p) => <td key={p}>{ehPlanoPago(p) ? precoEmReais(p, 'anual') : 'R$ 0'}</td>)}
                </tr>
                <tr>
                  <th scope="row">Equivale por mês</th>
                  {PLANOS.map((p) => <td key={p}>{ehPlanoPago(p) ? emReais(parcelaDoAnual(p)) : 'R$ 0'}</td>)}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </details>
  );
}

// aoComecarNoGratis: primeiro acesso, logo depois de criar o perfil. Planos aparece antes do Início, com o Grátis sem custo.
export function Planos({ sessao, perfil, inicial, cicloInicial, aoComecarNoGratis }: {
  sessao: Sessao; perfil: PerfilConta; inicial: Plano | null; cicloInicial: Ciclo; aoComecarNoGratis?: () => void;
}) {
  const { eu, erro } = useEu(sessao);
  const vigente = eu?.plano ?? perfil.plano;
  const [aba, setAba] = useState<Plano>(inicial ?? (vigente === 'gratis' ? 'prata' : vigente));
  const [ciclo, setCiclo] = useState<Ciclo>(cicloInicial);
  useEffect(() => {
    medir('view_item_list', { item_list_id: ciclo, item_list_name: 'Planos ' + ciclo, items: A_VENDA.map((p) => itemDoPlano(p, ciclo)) });
  }, [ciclo]);
  const computador = useComputador();
  // O toque em ASSINAR cria o Checkout na hora; o erro aparece embaixo do botão.
  const [ocupado, setOcupado] = useState<PlanoPago | null>(null);
  const [falha, setFalha] = useState<{ plano: Plano; texto: string } | null>(null);
  async function assinar(plano: PlanoPago) {
    setFalha(null);
    setOcupado(plano);
    toqueEmAssinar('planos');
    // Só cartão: a Stripe de conta brasileira só recebe Pix avulso (os pacotes de Tentos). O Checkout volta para /planos/ok.
    try { await irParaCheckout(sessao, plano, ciclo, 'planos'); } catch (e) { setFalha({ plano, texto: (e as Error).message }); setOcupado(null); }
  }
  const assinatura = eu?.assinatura && eu.assinatura.status !== 'aguardando_pix' ? eu.assinatura : null;
  const nota = assinatura?.ciclo === 'anual' && ciclo === 'mensal'
    ? `Seu plano é anual${assinatura.fimDoPeriodo ? ' até ' + dataCompleta(assinatura.fimDoPeriodo) : ''}. Para voltar ao mensal, cancele a renovação e assine o mensal quando o ano acabar.`
    : null;

  // O botão de cada plano, conforme o ciclo e a situação da conta. No celular, um CTA por tela, o do plano da aba;
  // no computador, um em cada cartão, com o laranja só no plano em destaque.
  function botao(plano: Plano, principal: boolean): ReactNode {
    const classe = (principal ? 'btn1' : 'btn2') + ' largo';
    const preco = ehPlanoPago(plano) ? (ciclo === 'anual' ? emReais(parcelaDoAnual(plano)) : precoEmReais(plano)) : '';
    const mesmo = assinatura ? assinatura.plano === plano && assinatura.ciclo === ciclo : plano === vigente;
    if (!ehPlanoPago(plano) && aoComecarNoGratis) return <button className={classe} onClick={aoComecarNoGratis}>COMEÇAR NO GRÁTIS</button>;
    if (!ehPlanoPago(plano)) {
      return vigente === 'gratis'
        ? <button className={classe} disabled>SEU PLANO</button>
        : <button className="btn2 largo" onClick={() => ir({ tela: 'meu-plano' })}>Ver meu plano</button>;
    }
    if (mesmo) return <button className={classe} disabled>SEU PLANO</button>;
    if (!A_VENDA.includes(plano)) return <button className={classe} disabled>{NOME_DO_PLANO[plano].toUpperCase()} EM BREVE</button>;
    if (eu?.cobranca === 'desligada' || (!eu && erro)) return <button className={classe} disabled>ASSINATURAS EM BREVE</button>;
    if (assinatura?.ciclo === 'anual' && ciclo === 'mensal') return <button className={classe} disabled>SEU PLANO É ANUAL</button>;
    if (assinatura) {
      const sobe = ordemDoPlano(plano) > ordemDoPlano(assinatura.plano);
      const rotulo = assinatura.plano === plano ? 'MUDAR PARA O ANUAL'
        : `${sobe ? 'SUBIR' : 'TROCAR'} PARA ${NOME_DO_PLANO[plano].toUpperCase()}${ciclo !== assinatura.ciclo ? ' ANUAL' : ''}`;
      return <button className={classe} onClick={() => ir({ tela: 'assinar', plano, ciclo })}>{rotulo} <small>{preco}/mês</small></button>;
    }
    return (
      <button className={classe} disabled={!eu || ocupado !== null} onClick={() => void assinar(plano)}>
        {ocupado === plano ? 'UM INSTANTE...' : <>ASSINAR {NOME_DO_PLANO[plano].toUpperCase()}{ciclo === 'anual' ? ' ANUAL' : ''} <small>{preco}/mês</small></>}
      </button>
    );
  }
  // Embaixo do botão: o erro e a linha da Stripe com os Termos.
  function depoisDoBotao(plano: Plano): ReactNode {
    if (!ehPlanoPago(plano) || assinatura) return null;
    return (
      <>
        {falha?.plano === plano && <p className="erro" role="alert">{falha.texto}</p>}
        <p className="nota centro linha-stripe">No cartão, pela Stripe. Cancele quando quiser. Ao assinar, você aceita os <a href="/termos">Termos de uso</a>.</p>
      </>
    );
  }
  const notaDoCiclo = ciclo === 'anual'
    ? 'Anual: à vista, uma vez por ano, no cartão. Cancele quando quiser: o plano vale até o fim do ano pago. Desistiu em até 7 dias? Devolvemos o valor.'
    : 'Mensal: no cartão, com renovação automática. Cancele quando quiser, sem multa. Desistiu em até 7 dias? Devolvemos o valor.';
  const titulo = aoComecarNoGratis ? (
    <>
      <Topo titulo="Como você quer começar?" marca />
      <p className="sub">O Grátis não custa nada. Assine quando quiser, e troque de plano a qualquer hora.</p>
    </>
  ) : <Topo titulo="Escolha seu plano" voltar={voltar} marca />;

  if (computador) {
    return (
      <Tela larga>
        {titulo}
        <div className="planos-cab">
          <AbasCiclo ciclo={ciclo} aoMudar={setCiclo} />
          {vigente !== 'gratis' && <p className="nota">Seu plano hoje: {NOME_DO_PLANO[vigente]}{assinatura ? ' ' + assinatura.ciclo : ''}.</p>}
        </div>
        <div className="planos-lado">
          {PLANOS.map((p) => (
            <CartaoPlano key={p} plano={p} ciclo={ciclo} destaque={p === 'prata'} escolhido={p === aba} evidencia={p === 'bronze' && vigente === 'gratis'}>
              <div className="pe-plano">{botao(p, p === aba)}{depoisDoBotao(p)}</div>
            </CartaoPlano>
          ))}
        </div>
        <p className="nota">{notaDoCiclo}</p>
        {nota && <p className="nota">{nota}</p>}
        <Comparar ciclo={ciclo} aberta />
      </Tela>
    );
  }

  return (
    <Tela>
      {titulo}
      <div className="duas-colunas">
        <div className="col">
          <AbasCiclo ciclo={ciclo} aoMudar={setCiclo} />
          <div className="abas-planos" role="tablist" aria-label="Planos">
            {PLANOS.map((p) => (
              <button key={p} role="tab" id={'aba-' + p} aria-selected={aba === p} aria-controls="painel-plano"
                className={'aba plano-' + p + (aba === p ? ' on' : '')} onClick={() => setAba(p)}>
                {p !== 'gratis' && <i aria-hidden="true" />}{NOME_DO_PLANO[p]}
              </button>
            ))}
          </div>
          <div id="painel-plano" role="tabpanel" aria-labelledby={'aba-' + aba}>
            <CartaoPlano plano={aba} ciclo={ciclo} destaque={aba === 'prata'} evidencia={aba === 'bronze' && vigente === 'gratis'} />
          </div>
        </div>
        <div className="col">
          {vigente !== 'gratis' && <p className="nota centro">Seu plano hoje: {NOME_DO_PLANO[vigente]}{assinatura ? ' ' + assinatura.ciclo : ''}.</p>}
          <Comparar ciclo={ciclo} />
          <p className="nota centro">{notaDoCiclo}</p>
          {nota && <p className="nota centro">{nota}</p>}
          <div className="rodape-cta">
            {botao(aba, true)}
            {depoisDoBotao(aba)}
            {aoComecarNoGratis && ehPlanoPago(aba) && <button className="btn2 largo" onClick={aoComecarNoGratis}>Começar no Grátis</button>}
          </div>
        </div>
      </div>
    </Tela>
  );
}
